import { MessageSquare, Pin } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext, useLocation } from 'react-router-dom';
import { Eyebrow, PageHeading } from '../../design-system';
import styles from './ChatHistoryPage.module.css';
import { useChatSessions, ChatSession } from '../../context/ChatSessionsContext';

interface OutletContext {
  selectedSessionId: number | null;
  setSelectedSessionId: (id: number | null) => void;
  refreshSessions: () => void;
}

export const ChatHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const chatPath = location.pathname.startsWith('/enterprise/')
    ? '/enterprise/dashboard/chat'
    : location.pathname.startsWith('/startup/')
    ? '/startup/dashboard/chat'
    : location.pathname.startsWith('/government/')
    ? '/government/dashboard/chat'
    : location.pathname.startsWith('/admin/')
    ? '/admin/dashboard/chat'
    : '/app';
  const outletContext = useOutletContext<OutletContext | null>();
  const setSelectedSessionId: OutletContext['setSelectedSessionId'] =
    outletContext?.setSelectedSessionId ?? (() => undefined);
  const {
    pinnedSessions: pinnedChats,
    unpinnedSessions: unpinnedChats,
    loading,
    error,
    refreshSessions,
    updateTitle,
    deleteSession,
    togglePin,
  } = useChatSessions();

  const [editingId, setEditingId]         = useState<number | null>(null);
  const [editingTitle, setEditingTitle]   = useState('');
  const [currentPage, setCurrentPage]     = useState<number>(1);
  const [search, setSearch]               = useState('');
  const PAGE_SIZE = 10;

  useEffect(() => {
    refreshSessions();
  }, [refreshSessions]);

  // Reset page when search changes
  useEffect(() => { setCurrentPage(1); }, [search]);
  useEffect(() => { setCurrentPage(1); }, [unpinnedChats.length]);

  const handleSaveTitle = async (id: number) => {
    if (!editingTitle.trim()) { setEditingId(null); return; }
    try {
      await updateTitle(id, editingTitle);
      setEditingId(null);
    } catch {
      alert('Failed to update chat title. Please try again.');
    }
  };

  const handleDelete = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Delete this chat? This cannot be undone.')) return;
    try {
      await deleteSession(id);
    } catch {
      alert('Failed to delete chat.');
    }
  };

  const handleTogglePin = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try { await togglePin(id); } catch { /* ignore */ }
  };

  const handleOpen = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    // Set the shared context (works where a dashboard shell provides it, e.g.
    // enterprise) AND pass the id in the URL. The admin dashboard renders Chat
    // without that shared context, so without the ?session= param the opened
    // chat showed no messages — the URL param makes Open work everywhere.
    setSelectedSessionId(id);
    navigate(`${chatPath}?session=${id}`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now  = new Date();
    const diff = Math.floor((now.getTime() - date.getTime()) / 86_400_000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    if (diff < 7)  return date.toLocaleDateString('en-US', { weekday: 'short' });
    return date.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  const formatTime = (dateString: string) =>
    new Date(dateString).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

  // All chats in display order: pinned first, then unpinned
  const filteredAll = [...pinnedChats, ...unpinnedChats].filter(c =>
    c.title.toLowerCase().includes(search.toLowerCase())
  );
  const totalPages = Math.max(1, Math.ceil(filteredAll.length / PAGE_SIZE));
  const pageChats  = filteredAll.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const renderRow = (chat: ChatSession) => {
    const isEditing = editingId === chat.id;
    return (
      <tr
        key={chat.id}
        className={`${styles.tableRow} ${chat.is_pinned ? styles.rowPinned : ''}`}
      >
        <td className={styles.tdPin}>
          {chat.is_pinned && <span className={styles.pinDot} title="Pinned"><Pin size={16} aria-hidden /></span>}
        </td>
        <td className={styles.tdTitle}>
          {isEditing ? (
            <div className={styles.inlineEdit} onClick={e => e.stopPropagation()}>
              <input
                autoFocus
                className={styles.editInput}
                value={editingTitle}
                onChange={e => setEditingTitle(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleSaveTitle(chat.id);
                  if (e.key === 'Escape') setEditingId(null);
                }}
              />
              <button className={`${styles.btn} ${styles.btnSave}`} onClick={() => handleSaveTitle(chat.id)}>Save</button>
              <button className={`${styles.btn} ${styles.btnCancel}`} onClick={() => setEditingId(null)}>✕</button>
            </div>
          ) : (
            <span className={styles.titleText}>{chat.title}</span>
          )}
        </td>
        <td className={styles.tdDate}>{formatDate(chat.updated_at || chat.created_at)}</td>
        <td className={styles.tdTime}>{formatTime(chat.updated_at || chat.created_at)}</td>
        <td className={styles.tdActions} onClick={e => e.stopPropagation()}>
          <div className={styles.rowActions}>
            <button className={`${styles.btn} ${styles.btnOpen}`}   onClick={e => handleOpen(chat.id, e)} title="Open in chat">Open</button>
            <button className={`${styles.btn} ${styles.btnPin}`}    onClick={e => handleTogglePin(chat.id, e)} title={chat.is_pinned ? 'Unpin' : 'Pin'}>{chat.is_pinned ? 'Unpin' : 'Pin'}</button>
            <button className={`${styles.btn} ${styles.btnEdit}`}   onClick={e => { e.stopPropagation(); setEditingId(chat.id); setEditingTitle(chat.title); }} title="Rename">Edit</button>
            <button className={`${styles.btn} ${styles.btnDelete}`} onClick={e => handleDelete(chat.id, e)} title="Delete">Delete</button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className={styles.root}>
      {/* ── Page header ───────────────────────────── */}
      <div className={styles.pageHeader}>
        <div className={styles.pageHeaderLeft}>
          <svg className={styles.pageHeaderIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          <div>
            <Eyebrow tone="default" dot>CHATS</Eyebrow>
            <PageHeading
              accentWord="History"
              accentTone="orange"
              size="h2"
              align="left"
              level="h1"
              className={styles.pageTitle}
            >
              Chat History
            </PageHeading>
          </div>
        </div>
        <div className={styles.pageHeaderRight}>
          <button className={styles.newChatBtn} onClick={() => navigate(chatPath)}>
            + New Chat
          </button>
        </div>
      </div>

      {/* ── Two-column body ─────────────────────── */}
      <div className={styles.body}>
        {/* LEFT: session table */}
        <div className={styles.listPanel}>
          {/* Search */}
          <div className={styles.searchBar}>
            <svg className={styles.searchIcon} viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M12.9 14.32a8 8 0 111.41-1.41l4.38 4.38-1.41 1.41-4.38-4.38zm-4.9.68a6 6 0 100-12 6 6 0 000 12z" clipRule="evenodd" />
            </svg>
            <input
              className={styles.searchInput}
              placeholder="Search conversations…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button className={styles.searchClear} onClick={() => setSearch('')}>×</button>
            )}
          </div>

          {/* Table */}
          {loading ? (
            <div className={styles.centred}><div className={styles.spinner} /></div>
          ) : error ? (
            <div className={styles.centred}>
              <p className={styles.errorText}>{error}</p>
              <button className={`${styles.btn} ${styles.btnOpen}`} onClick={refreshSessions}>Retry</button>
            </div>
          ) : filteredAll.length === 0 ? (
            <div className={styles.centred}>
              <div className={styles.emptyIcon}><MessageSquare size={20} aria-hidden /></div>
              <p className={styles.emptyText}>{search ? 'No results found.' : 'No chats yet.'}</p>
              {!search && (
                <button className={`${styles.btn} ${styles.btnOpen}`} onClick={() => navigate(chatPath)}>Start a Chat</button>
              )}
            </div>
          ) : (
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr className={styles.tableHead}>
                    <th className={styles.thPin}></th>
                    <th className={styles.thTitle}>Conversation</th>
                    <th className={styles.thDate}>Date</th>
                    <th className={styles.thTime}>Time</th>
                    <th className={styles.thActions}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageChats.map(renderRow)}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {filteredAll.length > PAGE_SIZE && (
            <div className={styles.pagination}>
              <button className={styles.pageBtn} disabled={currentPage === 1}       onClick={() => setCurrentPage(p => p - 1)}>← Previous</button>
              <span className={styles.pageInfo}>Page {currentPage} of {totalPages}</span>
              <button className={styles.pageBtn} disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}>Next →</button>
            </div>
          )}
        </div>


      </div>
    </div>
  );
};
